import React, { useState, useCallback } from 'react'
import _get from 'lodash/get'
import { Stack, Typography, Grid, styled, Paper, Box } from '@mui/material'
import {
  BooleanInput,
  DateTimeInput,
  required,
  maxLength,
  TextInput,
  useEditContext,
  Loading,
  useTranslate,
  FormTab,
  useLocaleState,
  TransformData,
} from 'react-admin'
import { ImageField, MultipleImageField } from '../../components/fields'
import dayjs from 'dayjs'
import { isAfterDate, isBeforeDate, isSameDate, isYoutubeLink } from '../../utils/validators'
import { TabbedMultiCardForm } from '../../components/tab'
import ModifyTimeStamp from '../../components/ModifyTimeStamp'
import { EditBase } from '../../components/base'
import { COMMON_FIELDS, PROMOTIONS_FIELDS } from '../../providers/localization/constant'
import { ApproveAndPublishToolBar } from '../../components/form/'
import { MyRichTextInput } from '../../components/text-editor'
import { ImageInput } from '../../components/inputs'
import ApplyToEnglishButton from '../../components/buttons/ApplyToEnglishButton'
import {
  ImagesInputsProps,
  PublishPeriodInputsProps,
  ThumbnailInputsProps,
  EditApplyEnglishButtonProps,
} from './type'
import { PromotionPublishNote } from '../../components/domains/promotion/PromotionPublishNote'
import { ThumbnailInput } from '../../components/inputs/ThumbnailInput'
import sanitizeHtml from 'sanitize-html'
import trim from 'lodash/trim'
import unescape from 'lodash/unescape'

const RESOURCE_PREFIX = 'resources.promotions.edit'

const PaperStyled = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: 120,
  lineHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .RaImageField-image': {
    verticalAlign: 'middle',
  },
}))

const PromotionImagesInputs: React.FC<ThumbnailInputsProps> = ({
  isPublished = false,
  record,
  field,
  source,
  label,
}) => {
  const translate = useTranslate()
  const image = _get(record, field)

  return isPublished ? (
    <>
      <Typography variant='caption' color='common.grey'>
        {translate(label)}
      </Typography>
      <PaperStyled variant='outlined'>
        {image ? (
          <ImageField source={field} title='title' />
        ) : (
          <Typography variant='caption' sx={{ color: 'error.main' }}>
            {translate(`${PROMOTIONS_FIELDS}.no_image`)}
          </Typography>
        )}
      </PaperStyled>
    </>
  ) : (
    <ThumbnailInput source={source} label={label} accept='image/*' fallbackSource={'full_path'}>
      <ImageField source='src' title='title' />
    </ThumbnailInput>
  )
}

const PromotionMultipleImagesInputs: React.FC<ImagesInputsProps> = ({
  isPublished = false,
  record,
  source,
  label,
}) => {
  const translate = useTranslate()
  const image = _get(record, source)

  return isPublished ? (
    <>
      <Typography variant='caption' color='common.grey'>
        {translate(label)}
      </Typography>
      <Box>
        {/* show images not found layout when images is an empty array */}
        {image && image.length > 0 ? (
          <MultipleImageField source={source} title='name' target='_blank' />
        ) : (
          <PaperStyled variant='outlined'>
            <Typography variant='caption' sx={{ color: 'error.main' }}>
              {translate(`${PROMOTIONS_FIELDS}.no_image`)}
            </Typography>
          </PaperStyled>
        )}
      </Box>
    </>
  ) : (
    <ImageInput
      source='images'
      label={`${PROMOTIONS_FIELDS}.images.label`}
      helperText={`${PROMOTIONS_FIELDS}.images_helper`}
      accept='image/*'
      deleteFileField='delete_images'
      fallbackSource='full_path'
      multiple
      initCrop={false}
    ></ImageInput>
  )
}

const CustomToolbar: React.FC = () => {
  return (
    <ApproveAndPublishToolBar encType='multipart/form-data'>
      <PromotionPublishNote />
    </ApproveAndPublishToolBar>
  )
}

const PublishPeriodInputs: React.FC<PublishPeriodInputsProps> = ({ isPublished = false }) => {
  const defaultPublishDate = dayjs()
  const defaultExpiredDate = defaultPublishDate.add(2, 'day')

  return (
    <>
      <DateTimeInput
        source='start_date'
        label={`${COMMON_FIELDS}.date.published_on`}
        disabled={isPublished}
        fullWidth
        defaultValue={defaultPublishDate}
        validate={[
          required(),
          isBeforeDate('end_date', 'The publish date must be before the expired date.'),
        ]}
      />
      <DateTimeInput
        source='end_date'
        disabled={isPublished}
        label={`${COMMON_FIELDS}.date.expired_on`}
        fullWidth
        defaultValue={defaultExpiredDate}
        validate={[
          required(),
          isAfterDate('start_date', 'The expired date must be after the publish date.'),
          !isPublished ? isSameDate() : null,
        ]}
      />
    </>
  )
}

const EditApply: React.FC<EditApplyEnglishButtonProps> = ({ isPublished, updateKeyEditor }) => {
  return !isPublished ? (
    <Grid item xs={12}>
      <ApplyToEnglishButton updateKeyEditor={updateKeyEditor} />
    </Grid>
  ) : (
    <></>
  )
}

const PersistForm: React.FC<{ preventEdit: boolean }> = ({ preventEdit = false }) => {
  const [activeLocale] = useLocaleState()
  const { record } = useEditContext()

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} order={{ xs: -1, sm: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <PublishPeriodInputs isPublished={preventEdit} />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextInput
              sx={{ display: 'none' }}
              source='order'
              label={`${COMMON_FIELDS}.order`}
              fullWidth
              defaultValue={0}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item sm={6}>
            <BooleanInput
              source='is_max_card'
              label={`${PROMOTIONS_FIELDS}.max_card.label`}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item sm={6}>
            <BooleanInput
              source='is_credit_card'
              label={`${PROMOTIONS_FIELDS}.credit_card.label`}
              disabled={preventEdit}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='youtube_link'
              label='Youtube Link'
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[isYoutubeLink()]}
              disabled={preventEdit}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source='slug_th'
          label={`${COMMON_FIELDS}.slug_th.label`}
          InputProps={{
            fullWidth: true,
          }}
          fullWidth
          disabled={preventEdit}
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source='slug_en'
          label={`${COMMON_FIELDS}.slug_en.label`}
          InputProps={{
            fullWidth: true,
          }}
          fullWidth
          disabled={preventEdit}
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <PromotionImagesInputs
              isPublished={preventEdit}
              record={record}
              source='thumbnail'
              field='thumbnail.full_path'
              label={`${PROMOTIONS_FIELDS}.thumbnail_path_helper`}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <PromotionMultipleImagesInputs
          source='images'
          isPublished={preventEdit}
          record={record}
          label={`${PROMOTIONS_FIELDS}.images.label`}
        />
      </Grid>
      <Grid item xs={12}>
        <ModifyTimeStamp language={activeLocale} />
      </Grid>
    </Grid>
  )
}

const EditForm: React.FC = () => {
  const { record, isLoading } = useEditContext()
  const [editorDescriptionEnKey, setEditorContentEnKey] = useState(0)
  const isPublished: boolean = record?.is_published
  const preventEdit = isPublished
  const reloadTextEditor = (): void => {
    setEditorContentEnKey(editorDescriptionEnKey + 1)
  }
  if (isLoading) return <Loading />

  return (
    <TabbedMultiCardForm
      formProps={{ toolbar: null, toolbarBottom: <CustomToolbar /> }}
      childrenForms={[<PersistForm key='' preventEdit={preventEdit} />]}
    >
      <FormTab label='TH'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='title_th'
              label={`${PROMOTIONS_FIELDS}.title_th`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[required(), maxLength(255)]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source='short_description_th'
              label={`${PROMOTIONS_FIELDS}.short_description_th`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              multiline
              rows={3}
              validate={[required()]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <MyRichTextInput
              fullWidth
              label={`${PROMOTIONS_FIELDS}.description_th`}
              source='description_th'
              validate={[required()]}
              disabled={preventEdit}
            />
          </Grid>
          <EditApply isPublished={isPublished} updateKeyEditor={reloadTextEditor} />
        </Grid>
      </FormTab>
      <FormTab label='EN'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='title_en'
              label={`${PROMOTIONS_FIELDS}.title_en`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[required(), maxLength(255)]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source='short_description_en'
              label={`${PROMOTIONS_FIELDS}.short_description_en`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              multiline
              rows={3}
              validate={[required()]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <MyRichTextInput
              key={editorDescriptionEnKey}
              fullWidth
              label={`${PROMOTIONS_FIELDS}.description_en`}
              source='description_en'
              validate={[required()]}
              disabled={preventEdit}
            />
          </Grid>
          <EditApply isPublished={isPublished} updateKeyEditor={reloadTextEditor} />
        </Grid>
      </FormTab>
    </TabbedMultiCardForm>
  )
}

const PromotionEdit: React.FC = () => {
  const transform: TransformData = useCallback((data) => {
    return {
      ...data,
      title_th: sanitizeHtml(unescape(data.title_th)),
      title_en: sanitizeHtml(unescape(data.title_en)),
      short_description_th: sanitizeHtml(unescape(data.short_description_th)),
      short_description_en: sanitizeHtml(unescape(data.short_description_en)),
      description_th: sanitizeHtml(unescape(data.description_th)),
      description_en: sanitizeHtml(unescape(data.description_en)),
      slug_th: trim(data.slug_th),
      slug_en: trim(data.slug_en),
    }
  }, [])
  return (
    <EditBase
      title={`${RESOURCE_PREFIX}.title`}
      listAction={{
        hasBackButton: true,
      }}
      noCard
      encType='multipart/form-data'
      transform={transform}
    >
      <EditForm />
    </EditBase>
  )
}

export default PromotionEdit
